import {
  ClinicCreationDto,
  ClinicResponseDto,
  ClinicUpdateDto,
  CommonEntitiesListResponse,
  Insurance,
  SearchProfessionalsByFiltersResponseDto,
  SearchProfessionalsByFiltersSearchCriteriaDto,
  SpecializationBriefResponse,
} from '@docbay/schemas';
import axiosInstance from 'integration/api/axiosInstance';

/**
 * API to handle all clinics related operations
 */
export const ClinicsAPI = {
  /**
   * Fetches all clinics with specified includes.
   *
   * @returns A promise that resolves to a CommonEntitiesListResponse of ClinicResponseDto.
   */
  fetchAllClinics: () =>
    axiosInstance.get<CommonEntitiesListResponse<ClinicResponseDto>>(
      '/clinics?include=address,photos,mainPhoto,professionalsRelations,owner',
    ),

  /**
   * Removes a clinic profile by its id.
   *
   * @param id - The id of the clinic to be removed.
   * @returns A promise that resolves to void.
   */
  removeProfile: (id: string) => axiosInstance.delete(`/clinics/${id}`),

  /**
   * Fetches a single clinic profile by its id with specified includes.
   *
   * @param id - The id of the clinic to be fetched.
   * @returns A promise that resolves to ClinicResponseDto.
   */
  getClinicProfile: (id: string) =>
    axiosInstance.get<ClinicResponseDto>(
      `/clinics/${id}?include=photos,contacts,professionalsRelations,insurances,professionalsRelations.professional,professionalsRelations.professional.mainPhoto,owner`,
    ),

  /**
   * Creates a new clinic profile with the provided data.
   *
   * @param data - The data to be used for creating the new clinic profile.
   * @returns A promise that resolves to ClinicResponseDto.
   */
  createClinicProfileData: (data: ClinicCreationDto) =>
    axiosInstance.post<ClinicResponseDto>('/clinics', data),

  /**
   * Updates an existing clinic profile with the provided id and data.
   *
   * @param id - The id of the clinic to be updated.
   * @param data - The data to be used for updating the existing clinic profile.
   * @returns A promise that resolves to ClinicResponseDto.
   */
  saveClinicProfileData: (id: string, data: ClinicUpdateDto) =>
    axiosInstance.patch<ClinicResponseDto>(`/clinics/${id}`, data),

  /**
   * Fetches all insurances with specified includes.
   *
   * @returns A promise that resolves to a CommonEntitiesListResponse of Insurance.
   */
  fetchInsurances: () =>
    axiosInstance.get<CommonEntitiesListResponse<Insurance>>('/insurances'),

  /**
   * Searches for professionals by filters.
   *
   * @param data - The search criteria to be used for searching professionals.
   * @returns A promise that resolves to an array of SearchProfessionalsByFiltersResponseDto.
   */
  searchProfessionalsByFilters: (
    data: SearchProfessionalsByFiltersSearchCriteriaDto,
  ) =>
    axiosInstance.post<SearchProfessionalsByFiltersResponseDto[]>(
      '/clinics/search-professionals-by-filters',
      data,
    ),

  /**
   * Fetches all specializations by a specific clinic id.
   *
   * @param clinicId - The id of the clinic to be used for fetching specializations.
   * @returns A promise that resolves to an array of SpecializationBriefResponse.
   */
  getSpecializationsByClinic: (clinicId: string) =>
    axiosInstance.get<SpecializationBriefResponse[]>(
      `/clinics/${clinicId}/specializations`,
    ),
};
